import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { lazy, Suspense, forwardRef } from "react";
import Loading from "./components/Loading";
import { useState, createContext, useEffect } from "react";
import Footer from "./components/Footer/Footer";
import { Route, Routes } from "react-router-dom";
import WishList from "./pages/Wishlist/Wishlist";
// import { onAuthStateChanged } from "firebase/auth";
import MuiAlert from "@mui/material/Alert";
import { Snackbar, Fade } from "@mui/material";
import Cart from "./pages/Cart/Cart";
import Product from "./pages/Product/Product";
import Auth from "./Auth";
import Products from "./pages/Products/Products";
import Orders from "./pages/MyAccount/Orders";
import Invoices from "./pages/MyAccount/Invoices";
import InvoiceDetails from "./pages/MyAccount/InvoiceDetails";
import OrderDetails from "./pages/MyAccount/OrderDetails";
import Accounts from "./pages/Admin/Accounts";
import Categories from "./pages/Admin/Categories";
import StoreDashboard from "./pages/Admin/StoreDashboard";
import StoreOrderDetail from "./pages/Admin/StoreOrderDetail";
import TeamMembers from "./pages/Admin/TeamMembers";
import AcceptInvite from "./pages/Auth/AcceptInvite";
import StoreOrders from "./pages/Admin/StoreOrders";
import StoreFailedOrders from "./pages/Admin/StoreFailedOrders";
import StoreSyncStatus from "./pages/Admin/StoreSyncStatus";
import ResetPassword from "./pages/Auth/ResetPassword";
import { getUserCart } from "./components/Cart";
import ThanksForOrder from "./pages/Cart/ThanksForOrder";
import MostPurchasedProducts from "./pages/MyAccount/MostPurchasedProducts";
import TopNavBar from "./components/Navbar/TopNavBar";
import NotFound from "./pages/NotFound";
import Register from "./pages/Auth/Register";
import Settings from "./pages/Auth/Settings";
import Help from "./pages/Help/Help";
import StoreLogin from "./pages/Auth/StoreLogin";
import axios from "./axios";
import MyAccount from "./pages/MyAccount/MyAccount";
import Login from "./pages/Customer/Login";
import Logs from "./pages/Admin/Logs";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Crons from "./pages/Admin/Crons";
import ImpersonationBanner from "./components/ImpersonationBanner";
import 'moment/min/locales'
const { palette } = createTheme();
const { augmentColor } = palette;
const createColor = (mainColor) => augmentColor({ color: { main: mainColor } });

const Home = lazy(() => import("./pages/Home/Home"));
const Navbar = lazy(() => import("./components/Navbar/Navbar"));
export const DataContext = createContext();

// Determine active language — fall back to "en" when not set
const _activeLang = localStorage.getItem("lang") ?? window.storeSettings?.language ?? "en";

// Build resources: merge window.translations (from DB) and ensure the
// active language always has a translation namespace so i18next never
// falls back to a missing language and renders raw keys.
const _dbTranslations = window.translations ?? {};
const _resources = {
  ..._dbTranslations,
  // Guarantee the active language exists even if DB has no entry yet
  [_activeLang]: {
    translation: {
      ...(_dbTranslations[_activeLang]?.translation ?? {}),
    },
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources: _resources,
    lng: _activeLang,
    fallbackLng: "en",
    interpolation: {
      escapeValue: false
    }
  });

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function App() {
  const [wishList, setWishList] = useState([]);
  const [cartList, setCartList] = useState([]);
  const [auth, setAuth] = useState([]);
  const [themeColors, setThemeColors] = useState({
    primary_color: window.storeSettings.primary_color,
    secondary_color: window.storeSettings.secondary_color
  });
  const [searchValue, setSearchValue] = useState("");
  const [snackbar, setSnackbar] = useState({
    message: "",
    open: false,
    severity: "",
  });

  useEffect(() => {
    setAuth({
      currentUser: Auth.getUser(),
    });
    initTheme();
  }, []);

  useEffect(() => {
    let getCart = async () => {
      if (Auth.isAuthenticated()) {
        getUserCart().then(function (cart) {
          setCartList([...cart]);
        });
      }
    };

    getCart();
  }, [auth]);

  function initTheme() {
    if (!Auth.isAuthenticated()) return;

    let division = JSON.parse(localStorage.getItem('user'))?.Division ??
      JSON.parse(localStorage.getItem('user'))?.store_division;

    if (typeof division === "undefined" || division == null) return;

    let currentTheme = JSON.parse(localStorage.getItem('currentStoreTheme'));

    if (currentTheme == null) {
      axios.get("/exact/store/details/" + division).then(({ data }) => {
        window.storeSettings = data.data;

        loadTheme(window.storeSettings);

        localStorage.setItem('currentStoreTheme', JSON.stringify(window.storeSettings));

      });
    } else {
      window.storeSettings = currentTheme;

      loadTheme(currentTheme);
    }

  }

  function loadTheme(theme) {
    document.title = theme?.site_name ?? 'API Masters';
    document.querySelector('meta[name="description"]').setAttribute("content", theme?.site_description ?? 'API Masters');
    document.documentElement.style.setProperty('--primary', theme?.primary_color ?? '#2b749b');
    document.documentElement.style.setProperty('--secondary', theme?.secondary_color ?? '#2b749b');

    setThemeColors({
      primary_color: theme?.primary_color ?? '#2b749b',
      secondary_color: theme?.secondary_color ?? '#2b749b',
    });
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar({ ...snackbar, open: !snackbar.open });
  };

  var theme = createTheme({
    palette: {
      apimasters: createColor(themeColors.primary_color),
      primary: {
        main: createColor(themeColors.primary_color).main,
      },
      secondary: {
        main: createColor(themeColors.primary_color).main,
        // main: createColor(themeColors.secondary_color).main,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },
    },
  });

  return (
    <DataContext.Provider
      value={{
        auth,
        setAuth,
        wishList,
        setWishList,
        cartList,
        setCartList,
        snackbar,
        setSnackbar,
        searchValue,
        setSearchValue,
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Suspense fallback={<Loading />}>
          {Auth.isImpersonating() && <ImpersonationBanner />}
          <div>
            <TopNavBar />
            <Navbar />
          </div>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/wishlist" element={<WishList />} />
            <Route path="/products/:productID" element={<Product />} />
            <Route path="/products" element={<Products />} />
            <Route path="/category/:categoryID" element={<Products />} />
            {Auth.isAuthenticated() && <>
              <Route path="/cart" element={<Cart />} />
              <Route
                path="/cart/thanks-for-order/:order_number"
                element={<ThanksForOrder />}
              />
              {Auth.getUserType() === "normal" && <>
                <Route path="/my-account" element={<MyAccount />} />
                <Route
                  path="/my-account/most-purchased-products"
                  element={<MostPurchasedProducts />}
                />
                <Route path="/my-account/orders" element={<Orders />} />
                <Route path="/my-account/orders/:id" element={<OrderDetails />} />
                <Route path="/my-account/invoices" element={<Invoices />} />
                <Route
                  path="/my-account/invoices/:id"
                  element={<InvoiceDetails />}
                />
              </>}
              <Route path="/store/dashboard" element={<StoreDashboard />} />
              <Route path="/store/orders" element={<StoreOrders />} />
              <Route path="/store/orders/:id" element={<StoreOrderDetail />} />
              <Route path="/store/team" element={<TeamMembers />} />
              <Route path="/store/failed-orders" element={<StoreFailedOrders />} />
              <Route path="/store/sync-status" element={<StoreSyncStatus />} />
              <Route path="/store/accounts" element={<Accounts />} />
              <Route path="/store/logs" element={<Logs />} />
              <Route path="/store/crons" element={<Crons />} />
              <Route path="/store/categories" element={<Categories />} />
              <Route path="/exact/settings" element={<Settings />} />
              <Route path="/store/help" element={<Help />} />
            </>}
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/customer-login" element={<Login />} />
            <Route path="/exact/store-login" element={<StoreLogin />} />
            <Route path="/try-now" element={<Register />} />
            <Route path="/reset-password/:hash" element={<ResetPassword />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          <Footer />
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={handleClose}
            TransitionComponent={Fade}
          >
            <Alert
              onClose={handleClose}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Suspense>
      </ThemeProvider>
    </DataContext.Provider>
  );
}

export default App;
